import { useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Star } from "lucide-react";
import { toast } from "sonner";

export default function FeedbackWidget() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState("general");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!message.trim()) { toast.error("Please write a quick note"); return; }
    setBusy(true);
    try {
      await api.post("/feedback", { message, page: loc.pathname, rating: rating || null, category });
      toast.success("Thank you — we read every message.");
      setOpen(false);
      setMessage(""); setRating(0); setCategory("general");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white shadow-lg shadow-[#3A5A4A]/30 transition hover:translate-y-[-1px]"
        data-testid="feedback-trigger"
        aria-label="Send feedback"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="text-sm hidden sm:inline">Tell us how it's going</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">How's Hope Chest treating you?</DialogTitle>
            <DialogDescription>
              We read every word. Anything that's broken, confusing, or wonderful — tell us. We answer personally.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "want", l: "💡 A want", d: "Feature idea" },
                { v: "issue", l: "🐞 An issue", d: "Something broken" },
                { v: "praise", l: "💚 Praise", d: "Working great" },
              ].map((c) => (
                <button
                  key={c.v}
                  type="button"
                  onClick={() => setCategory(c.v)}
                  className={`text-left p-3 rounded-xl border transition ${category === c.v ? "border-[#3A5A4A] bg-[#D8E2D8]" : "border-[#EBE5DF] hover:bg-[#F3EFEA]"}`}
                  data-testid={`feedback-cat-${c.v}`}
                >
                  <p className="text-sm font-medium">{c.l}</p>
                  <p className="text-[11px] text-[#7A736E] mt-0.5">{c.d}</p>
                </button>
              ))}
            </div>
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`feedback-star-${n}`}
                  aria-label={`Rate ${n} stars`}
                >
                  <Star className={`w-7 h-7 ${rating >= n ? "fill-[#D9A441] text-[#D9A441]" : "text-[#EBE5DF]"}`} />
                </button>
              ))}
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's working? What's not? Anything you wish Hope Chest did?"
              className="rounded-xl min-h-[120px]"
              data-testid="feedback-message"
            />
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="feedback-submit">
              {busy ? "Sending…" : "Send to the team"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
